/**
 * Main Application Component
 * Lesson editor with block sidebar and content canvas
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@course-authoring/shared-types';
import { ProtectedRoute, SignupPage } from './auth';
import { AuthoringShell, UserManagementPage } from './components';
import { AdminRepublishPage } from './pages/AdminRepublishPage';
import { AssignedQuestionnairesPage } from './pages/AssignedQuestionnairesPage';
import { AuthorWithAiConversationPage } from './pages/authorWithAi/AuthorWithAiConversationPage';
import { AuthorWithAiLandingPage } from './pages/authorWithAi/AuthorWithAiLandingPage';
import { CourseEditorPage } from './pages/courseEditor/CourseEditorPage';
import { CourseLearnerPreviewPage } from './pages/CourseLearnerPreviewPage';
import { CourseListPage } from './pages/CourseListPage';
import { CreateLessonPage } from './pages/CreateLessonPage';
import { DeliveryStatusPage } from './pages/DeliveryStatusPage';
import { DesignLibraryPage } from './pages/DesignLibraryPage';
import { ImageLibraryPage } from './pages/ImageLibraryPage';
import { CenterstagePage } from './pages/centerstage/CenterstagePage';
import { LessonEditorPage } from './pages/lessonEditor/LessonEditorPage';
import { LessonListPage } from './pages/LessonListPage';
import { MapListPage } from './pages/MapListPage';
import { TagsPage } from './pages/TagsPage';
import { QuestionsPage } from './pages/QuestionsPage';
import { QuestionBankPage } from './pages/questionBank/QuestionBankPage';
import { ThemeSettingsPage } from './pages/themeSettings/ThemeSettingsPage';
import { CreateMapPage } from './pages/CreateMapPage';
import { MapEditorPage } from './pages/MapEditorPage';

export function App() {
  return (
    <ThemeProvider>
      <Routes>
        <Route path="/signup" element={<SignupPage />} />
        <Route path="*" element={
          <ProtectedRoute>
            <Routes>
              <Route path="/admin/republish" element={<AuthoringShell><AdminRepublishPage /></AuthoringShell>} />
              <Route
                path="/author-with-ai"
                element={
                  <AuthoringShell>
                    <AuthorWithAiLandingPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/author-with-ai/builds/:buildId"
                element={
                  <AuthoringShell>
                    <AuthorWithAiConversationPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/lessons/new"
                element={
                  <AuthoringShell>
                    <CreateLessonPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/lessons/:lessonId/delivery"
                element={
                  <AuthoringShell>
                    <DeliveryStatusPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/lessons/:lessonId"
                element={
                  <AuthoringShell>
                    <LessonEditorPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/lessons"
                element={
                  <AuthoringShell>
                    <LessonListPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/image-library"
                element={
                  <AuthoringShell>
                    <ImageLibraryPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/centerstage"
                element={
                  <AuthoringShell>
                    <CenterstagePage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/questions"
                element={
                  <AuthoringShell>
                    <QuestionBankPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/questions/manage"
                element={
                  <AuthoringShell>
                    <QuestionsPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/maps"
                element={
                  <AuthoringShell>
                    <MapListPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/maps/new"
                element={
                  <AuthoringShell>
                    <CreateMapPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/tags"
                element={
                  <AuthoringShell>
                    <TagsPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/maps/:mapId"
                element={
                  <AuthoringShell>
                    <MapEditorPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/users"
                element={
                  <AuthoringShell>
                    <UserManagementPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/courses/new"
                element={
                  <AuthoringShell>
                    <CourseEditorPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/courses/:courseId/preview"
                element={
                  <AuthoringShell>
                    <CourseLearnerPreviewPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/courses/:courseId/delivery"
                element={
                  <AuthoringShell>
                    <DeliveryStatusPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/courses/:courseId"
                element={
                  <AuthoringShell>
                    <CourseEditorPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/courses"
                element={
                  <AuthoringShell>
                    <CourseListPage />
                  </AuthoringShell>
                }
              />
              <Route
                path="/assigned-questionnaires"
                element={
                  <AuthoringShell>
                    <AssignedQuestionnairesPage />
                  </AuthoringShell>
                }
              />
              <Route path="/design-library" element={<DesignLibraryPage />} />
              <Route
                path="/theme-settings"
                element={
                  <AuthoringShell>
                    <ThemeSettingsPage />
                  </AuthoringShell>
                }
              />
              <Route path="*" element={<Navigate to="/lessons" replace />} />
            </Routes>
          </ProtectedRoute>
        } />
      </Routes>
    </ThemeProvider>
  );
}
